import { useRef } from 'react'
import { Link } from 'react-router-dom'
import AnimatedSection from '../components/common/AnimatedSection'
import AboutWhyChooseSection from '../components/about/AboutWhyChooseSection'
import HeroSlider from '../components/home/HeroSlider'
import SEO from '../components/common/SEO'
import { getProductHref } from '../data/productsCatalogData'
import {
  company,
  products,
  checkIcon as CheckIcon,
} from '../data/siteData'
import {
  FaArrowRight,
  FaBoxesStacked,
  FaChevronLeft,
  FaChevronRight,
  FaGlobe,
  FaNetworkWired,
  FaShieldHalved,
  FaShip,
  FaTruckFast,
} from 'react-icons/fa6'

const aboutSectionImage = '/assets/about-port.png'
const ctaSectionBg = '/assets/cta-section-bg.png'
const productsSectionBg = '/assets/hero-slide-2.png'

function Home() {
  const brandTrackRef = useRef(null)

  const scrollBrands = (direction) => {
    const track = brandTrackRef.current
    if (!track) return
    track.scrollBy({ left: direction * 280, behavior: 'smooth' })
  }

  const services = [
    {
      icon: FaGlobe,
      title: 'International Metal Trading',
      text: 'Structured global trading support.',
    },
    {
      icon: FaNetworkWired,
      title: 'Global Procurement',
      text: 'Verified supplier and sourcing network.',
    },
    {
      icon: FaBoxesStacked,
      title: 'Supply Chain Management',
      text: 'Optimized supply chain planning.',
    },
    {
      icon: FaTruckFast,
      title: 'Logistics Coordination',
      text: 'Coordinated dispatch and delivery.',
    },
    {
      icon: FaShieldHalved,
      title: 'Quality Assurance',
      text: 'Strict quality checks at each stage.',
    },
    {
      icon: FaShip,
      title: 'Import & Export',
      text: 'Reliable cross-border trade execution.',
    },
  ]

  const productOrder = ['Copper', 'Aluminium', 'Zinc', 'Brass', 'Stainless Steel', 'Lead']
  const premiumProducts = productOrder
    .map((name) => products.find((item) => item.name === name))
    .filter(Boolean)

  const aboutPoints = [
    'Global network of trusted suppliers',
    'Competitive pricing with uncompromised quality',
    'Timely delivery and strong logistics support',
    'Customer-centric approach and reliable service',
  ]

  const brands = [
    'TATA STEEL',
    'Vedanta',
    'JSW Steel',
    'Jindal Stainless',
    'Hindalco',
    'Aditya Birla',
  ]

  const ctaMetalLinks = ['Copper', 'Aluminium', 'Zinc', 'Stainless Steel', 'Lead']

  return (
    <>
      <SEO
        title="Home"
        description="Torbit Metals Private Limited is a professional industrial metal trading company offering sourcing, procurement, quality control and supply solutions."
      />

      <HeroSlider />

      <AnimatedSection className="corp-services-section">
        <div className="container corp-container">
          <header className="corp-services-heading">
            <div className="about-vision-label corp-services-heading__label">
              <span className="about-vision-label-line" aria-hidden="true" />
              <span>Main Services</span>
            </div>
            <h2 className="corp-services-heading__title">
              Our Core <span>Services</span>
            </h2>
            <span className="corp-services-heading__accent" aria-hidden="true" />
            <p className="corp-services-heading__lead">
              Structured trading, procurement, and logistics support for industrial metal supply.
            </p>
          </header>
          <div className="corp-services-grid" role="list">
            {services.map((service) => {
              const Icon = service.icon
              return (
                <article className="corp-service-card" key={service.title} role="listitem">
                  <div className="corp-service-card__body">
                    <span className="corp-service-icon" aria-hidden="true">
                      <Icon />
                    </span>
                    <h3>{service.title}</h3>
                    {/* <span className="corp-service-divider" aria-hidden="true" /> */}
                    <p>{service.text}</p>
                  </div>
                  {/* <span className="corp-service-accent" aria-hidden="true" /> */}
                </article>
              )
            })}
          </div>
        </div>
      </AnimatedSection>

      <AnimatedSection className="corp-products-section">
        <div className="corp-products-bg" aria-hidden="true">
          <img src={productsSectionBg} alt="" />
          <div className="corp-products-bg-overlay" />
        </div>

        <div className="corp-products-inner">
          <div className="container corp-container">
            <div className="corp-products-top">
              <div className="corp-products-heading">
                <span className="section-label">Our Products</span>
                <h2>
                  We Deal In <span>Premium</span> Metals
                </h2>
                <p>High-grade metals for industrial and international trade requirements.</p>
              </div>
              <Link className="corp-products-viewall" to="/products">
                View All Products
                <FaArrowRight />
              </Link>
            </div>

            <div className="corp-products-strip" role="list">
              {premiumProducts.map((product) => (
                <Link
                  className="corp-product-card"
                  key={product.name}
                  to={getProductHref(product.name)}
                  role="listitem"
                  aria-label={`View ${product.name} products`}
                >
                  <div className="corp-product-media">
                    <img src={product.image} alt={`${product.name} metal product`} loading="lazy" />
                  </div>
                  <div className="corp-product-meta">
                    <h3>{product.name}</h3>
                    <span className="corp-product-arrow" aria-hidden="true">
                      <FaArrowRight />
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </AnimatedSection>

      <AboutWhyChooseSection />

      <AnimatedSection className="corp-about-section">
        <div className="container corp-container">
          <div className="row g-5 align-items-center corp-about-row">
            <div className="col-lg-6 corp-about-copy">
              <span className="section-label">About Us</span>
              <h2 className="corp-about-title">
                Building Stronger Futures Through <span>Global Partnerships</span>
              </h2>
              <p className="corp-about-lead">
                {company.name} is trusted for international metal trading through reliable
                service, quality-focused execution, and long-term supply partnerships.
              </p>
              <ul className="corp-about-points">
                {aboutPoints.map((item) => (
                  <li key={item}>
                    <CheckIcon aria-hidden="true" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <Link className="corp-about-cta" to="/about">
                Read More About Us
                <FaArrowRight aria-hidden="true" />
              </Link>
            </div>

            <div className="col-lg-6">
              <div className="corp-about-visual">
                <span className="corp-about-dots" aria-hidden="true" />
                <span className="corp-about-frame" aria-hidden="true" />
                <img
                  src={aboutSectionImage}
                  alt="Industrial port with container ship and logistics operations"
                  loading="lazy"
                />
              </div>
            </div>
          </div>

          {/* <div className="corp-trusted-block">
            <span className="section-label corp-trusted-label">Trusted by Industry Leaders</span>
            <div className="corp-brand-carousel">
              <button
                type="button"
                className="corp-brand-nav"
                onClick={() => scrollBrands(-1)}
                aria-label="Scroll partners left"
              >
                <FaChevronLeft />
              </button>
              <div className="corp-brand-track" ref={brandTrackRef}>
                {brands.map((item) => (
                  <span className="corp-brand-item" key={item}>
                    {item}
                  </span>
                ))}
              </div>
              <button
                type="button"
                className="corp-brand-nav"
                onClick={() => scrollBrands(1)}
                aria-label="Scroll partners right"
              >
                <FaChevronRight />
              </button>
            </div>
          </div> */}
        </div>
      </AnimatedSection>

      {/* <AnimatedSection className="corp-cta-section">
        <div className="corp-cta-bg" aria-hidden="true">
          <img src={ctaSectionBg} alt="" />
          <div className="corp-cta-overlay" />
        </div>

        <div className="container corp-cta-inner">
          <div className="corp-cta-card">
            <h2>Looking for a Reliable Metal Trading Partner?</h2>
            <p className="corp-cta-subtitle">Let&apos;s Build Something Strong Together!</p>
            <Link className="corp-cta-btn" to="/contact">
              Get In Touch
            </Link>
          </div>

          <nav className="corp-cta-metals" aria-label="Featured metal categories">
            {ctaMetalLinks.map((metal) => (
              <Link className="corp-cta-metal-link" key={metal} to="/products">
                {metal}
                <FaArrowRight aria-hidden="true" />
              </Link>
            ))}
          </nav>
        </div>
      </AnimatedSection> */}
    </>
  )
}

export default Home
